import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import styled from "styled-components";
import {
  Modal,
  ModalBackground,
  ModalClose,
  ModalContent,
  ModalHeading,
} from "../library/Modal";
import Engagement from "../../apiResources/Engagement";
import EngagementsDisengage from "../../apiResources/EngagementsDisengage";
import { TextareaInputField, FormError } from "../library/Form";
import Button from "../library/Button";
import { useBoolean } from "../../hooks";

const DisengageJobModal = (props: Props) => {
  const { engagement, onDismiss, onSuccess } = props;
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useBoolean();

  const values = { message: "" };

  const onSubmit = async (data: ReturnType<() => typeof values>) => {
    try {
      setLoading.on();

      const disengagement = new EngagementsDisengage({
        message: data.message,
        engagementId: engagement.id,
      });

      const success = await disengagement.save();

      if (success) {
        onSuccess();
      } else {
        setFormError(disengagement.errors[0].fullMessage);
      }
    } catch (err) {
      setFormError("😬 Uh oh, something went wrong there!");
      Sentry.captureException(err);
    } finally {
      setLoading.off();
    }
  };

  return (
    <Modal isOpen onDismiss={onDismiss} id="disengage-modal">
      <ModalBackground onClick={onDismiss} />
      <ModalContent>
        <ModalHeading>Confirm Disengagement</ModalHeading>

        {formError && <FormError>{formError}</FormError>}

        <p>
          You are about to <strong>disengage</strong> from this employer.
        </p>

        <p>
          You will <strong>not be able to re-engage</strong> them to fill{" "}
          <strong>this particular job - {engagement.job.title}</strong>, but may
          engage them for other positions in future.
        </p>

        <p>Do you want to proceed?</p>

        <Formik
          initialValues={values}
          validationSchema={Yup.object({
            message: Yup.string()
              .required(
                "The disengagement message must be at least 30 characters"
              )
              .min(
                30,
                "The disengagement message must be at least 30 characters"
              )
              .max(
                300,
                "The disengagement message can not be longer than 300 characters"
              ),
          })}
          onSubmit={onSubmit}
        >
          <Form autoComplete="off">
            <TextareaInputField
              name="message"
              label="Message"
              maxLength={300}
              placeholder="Enter your message..."
            />
            <ButtonRow>
              <Button
                disabled={loading}
                primary
                name={!loading ? "Disengage" : "Disengaging..."}
                type="submit"
              />
              <Button
                tertiary
                name="Cancel"
                type="button"
                onClick={onDismiss}
              />
            </ButtonRow>
          </Form>
        </Formik>

        <ModalClose aria-label="Close modal" onClick={onDismiss} />
      </ModalContent>
    </Modal>
  );
};

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  > button:not(:last-child) {
    margin-right: 1rem;
  }
`;

type Props = {
  onSuccess: () => void;
  onDismiss: () => void;
  engagement: Engagement;
};

export default DisengageJobModal;
